import React from 'react';

import { VStack, Text, Badge, HStack, Box } from '@chakra-ui/react';

import { DetailField, DetailSection } from './DetailField';
import { useColors } from './colors';
import {
  formatOrderDate,
  formatMoney,
  getOrderCancelDetails,
  getOrderReturnDetails,
  orderMainStatusBadge,
  fulfillmentStatusBadge,
} from '../../utils/orderStatus.util';

const OverviewTab = ({ order }) => {
  const { subTextColor } = useColors();
  const isEbay = order.marketplace === 'ebay';
  const mainStatus = orderMainStatusBadge(order);
  const fulfillment = fulfillmentStatusBadge(order.orderFulfillmentStatus || order.orderStatus);
  const cancelDetails = getOrderCancelDetails(order);
  const returnDetails = getOrderReturnDetails(order);

  return (
    <VStack align="stretch" spacing={5}>
      <DetailSection title="Order identifiers">
        <DetailField label="Marketplace order ID" value={order.marketplaceOrderId} mono />
        {isEbay && (
          <>
            <DetailField label="Sales record reference" value={order.salesRecordReference} mono />
            <DetailField label="Legacy order ID" value={order.legacyOrderId} mono />
          </>
        )}
        {order.salesChannel && (
          <>
            <DetailField label="Marketplace ID" value={order.salesChannel.marketplaceId} mono />
            <DetailField label="Channel" value={order.salesChannel.channelName} />
          </>
        )}
        <DetailField label="Internal ID" value={order.id} mono />
        <DetailField label="Store" value={order.storeName} />
      </DetailSection>

      <DetailSection title="Status & timeline">
        <Box>
          <Text fontSize="xs" color={subTextColor} mb={0.5}>
            Order status
          </Text>
          <Badge colorScheme={mainStatus.colorScheme} variant="subtle">
            {mainStatus.label}
          </Badge>
        </Box>
        <Box>
          <Text fontSize="xs" color={subTextColor} mb={0.5}>
            Fulfillment status
          </Text>
          <Badge colorScheme={fulfillment.colorScheme} variant="subtle">
            {fulfillment.label}
          </Badge>
        </Box>
        {isEbay && (
          <DetailField label="Order payment status" value={order.orderPaymentStatus} />
        )}
        <DetailField label="Fulfillment channel" value={order.fulfillmentChannel} />
        <DetailField label="Created" value={formatOrderDate(order.createdAt)} />
        <DetailField
          label="Last modified"
          value={formatOrderDate(order.lastModifiedDate || order.lastUpdatedAt)}
        />
      </DetailSection>

      {returnDetails && (
        <DetailSection title="Return">
          <DetailField label="Return ID" value={returnDetails.returnId} mono />
          <DetailField label="Return status" value={returnDetails.status} />
          <DetailField label="Return reason" value={returnDetails.reason} />
          <DetailField label="Reason type" value={returnDetails.reasonType} />
          <DetailField label="Return type" value={returnDetails.type} />
          <DetailField label="Requested on" value={returnDetails.creationDate} />
          <DetailField label="Closed on" value={returnDetails.closedDate} />
          {returnDetails.refundAmount != null && (
            <DetailField
              label="Refund amount"
              value={formatMoney(returnDetails.refundAmount, returnDetails.refundCurrency)}
            />
          )}
          {returnDetails.comments && (
            <DetailField label="Buyer comments" value={returnDetails.comments} />
          )}
        </DetailSection>
      )}

      {cancelDetails && (
        <DetailSection title="Cancellation">
          <DetailField label="Cancel state" value={cancelDetails.cancelStateLabel} />
          <DetailField label="Cancel reason" value={cancelDetails.reason} />
          <DetailField label="Initiated by" value={cancelDetails.initiator} />
          <DetailField label="Canceled on" value={cancelDetails.cancelledDate} />
          {cancelDetails.requests.length > 1 &&
            cancelDetails.requests.map((request, index) => (
              <Box key={`cancel-request-${index}`} gridColumn={{ base: 'span 1', md: 'span 2' }}>
                <Text fontSize="xs" color={subTextColor} mb={2} fontWeight="medium">
                  Cancel request {index + 1}
                </Text>
                <Box
                  display="grid"
                  gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                  gap={4}
                >
                  <DetailField label="Reason" value={request.reason} />
                  <DetailField label="Initiated by" value={request.initiator} />
                  <DetailField label="Request state" value={request.state} />
                  <DetailField label="Requested on" value={request.requestedDate} />
                  <DetailField label="Completed on" value={request.completedDate} />
                </Box>
              </Box>
            ))}
        </DetailSection>
      )}

      {(order.programs?.length > 0 || order.isBusinessOrder != null) && (
        <DetailSection title="Programs & flags" columns={3}>
          <Box>
            <Text fontSize="xs" color={subTextColor} mb={1}>
              Programs
            </Text>
            <HStack flexWrap="wrap">
              {(order.programs || []).map((p) => (
                <Badge key={p} colorScheme="blue" variant="subtle">
                  {p}
                </Badge>
              ))}
              {(!order.programs || order.programs.length === 0) && (
                <Text fontSize="sm">—</Text>
              )}
            </HStack>
          </Box>
          <DetailField label="Business order" value={order.isBusinessOrder ? 'Yes' : 'No'} />
          <DetailField label="Gift order" value={order.isGift ? 'Yes' : 'No'} />
        </DetailSection>
      )}

      {order.buyerCheckoutNotes && (
        <DetailSection title="Buyer checkout notes" columns={1}>
          <DetailField label="Notes" value={order.buyerCheckoutNotes} />
        </DetailSection>
      )}

      {order.linkedSku && (
        <DetailSection title="Catalog linkage" columns={2}>
          <DetailField label="Canonical SKU" value={order.linkedSku.canonicalSku} mono />
          <DetailField label="Listing ID" value={order.linkedSku.listingId} mono />
        </DetailSection>
      )}
    </VStack>
  );
};

export default OverviewTab;
